import _jsx from "@babel/runtime/helpers/jsx";
/* global googletag */
import React, { memo, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Waypoint } from 'react-waypoint';
import { AdvertisementText } from 'reaxl';
import _isEqual from 'lodash/isEqual';
import { DFPContext } from '../DFPProvider/DFPProvider';
import { LAZYLOADING_METHODS } from '../constants';

/**
 *  The AdSlot is a child of the DFPProvider component.
 *  It processes the DFP slot and pushes it to the DFPProvider through context via addAdSlot().
 *  It will also process the adaptive sizeMapping(), slot level targeting, and handle lazy loading.
 **/

// Adapted from code provided by Adomik
const adomikRandomAdGroup = () => {
  const rand = Math.random();
  switch (false) {
    case !(rand < 0.09):
      return 'ad_ex' + Math.floor(100 * rand);
    case !(rand < 0.1):
      return 'ad_bc';
    default:
      return 'ad_opt';
  }
};
const findExistingSlot = slotId => googletag && googletag.apiReady && googletag.pubads().getSlots().find(slot => slot && slotId === slot.getSlotId().getDomId());
const WayPointAd = /*#__PURE__*/memo(_ref => {
  let {
    adSlotId,
    handleEnter,
    handleLeave,
    lazyLoadingTopOffset,
    lazyLoadingBottomOffset,
    renderAdContainer
  } = _ref;
  return /*#__PURE__*/_jsx(Waypoint, {
    onEnter: handleEnter,
    onLeave: handleLeave,
    topOffset: lazyLoadingTopOffset,
    bottomOffset: lazyLoadingBottomOffset
  }, `waypoint=${adSlotId}`, renderAdContainer());
}, (oldProps, newProps) => _isEqual(oldProps, newProps));
function AdSlot(_ref2) {
  let {
    advertisementLabel = {
      className: 'text-right',
      text: 'Advertisement',
      enableHide: false,
      hideButton: {},
      onClickHide: () => {}
    },
    asyncInit = 0,
    className,
    extra = {},
    lazyLoading = false,
    lazyLoadingBottomOffset,
    lazyLoadingMethod = LAZYLOADING_METHODS.Waypoint,
    lazyLoadingTopOffset,
    multiSize,
    multiSizeHandler = () => {},
    outOfPage = false,
    path,
    preserveMaxSize = false,
    // TODO set this to true after test
    showPlaceholder = true,
    size,
    slot,
    slotId,
    targeting,
    timeoutId
  } = _ref2;
  const context = useContext(DFPContext);
  const adSlotId = `div-gpt-ad-${slotId}`;
  const adSlotContainerId = `${adSlotId}-container`;
  const timeoutRef = useRef(timeoutId);
  const slotRef = useRef(slot);
  const showPlaceholderRef = useRef(showPlaceholder);
  const refresh = function (props) {
    if (props === void 0) {
      props = {};
    }
    const {
      isLazy
    } = props;
    // if the ad is lazy, then it will make a request on its own.
    // Otherwise it will wait for all other ads to render before making the request
    if (slotRef.current && slotRef.current.getSlotElementId) {
      const slotEleId = slotRef.current.getSlotElementId();
      context.addUnRefreshedAdCount();
      context.addAdSlot(slotRef.current);
      context.refreshAds({
        isLazy,
        slotId: slotEleId,
        slot: slotRef.current
      });
    }
  };
  const refreshLazyAd = () => {
    if (slotRef.current) {
      refresh({
        isLazy: true
      });
    } else {
      setTimeout(refreshLazyAd, 100);
    }
  };
  const handleResize = () => {
    multiSizeHandler();
  };
  const handleEnter = () => {
    refreshLazyAd();
    context.addToRenderedSlots(slotRef.current);
  };
  const handleLeave = () => {
    context.addToLazyLoadedAds(slotRef.current);
  };
  useEffect(() => {
    let pubads = null;
    const handleSlotRenderEnded = event => {
      if (event.isEmpty) {
        document.getElementById(event.slot?.getSlotElementId())?.parentNode?.classList?.remove('display-block');
        /* set the placeholder in the event of a re-render */
        showPlaceholderRef.current = true;
      }
    };
    const initSlot = () => {
      const slotPath = path || context.adUnit;
      googletag.cmd.push(() => {
        if (multiSize) {
          const adSize = multiSize.map(item => item[1]);
          let mapping = googletag.sizeMapping();
          for (let i = 0; i < multiSize.length; i += 1) {
            mapping.addSize(multiSize[i][0], multiSize[i][1]);
          }
          mapping = mapping.build();
          slotRef.current = findExistingSlot(adSlotId) || googletag.defineSlot(slotPath, adSize, adSlotId);
          slotRef.current = slotRef.current.defineSizeMapping(mapping).addService(googletag.pubads());
        } else if (outOfPage) {
          slotRef.current = googletag.defineOutOfPageSlot(slotPath, adSlotId).addService(googletag.pubads());
        } else {
          slotRef.current = findExistingSlot(adSlotId) || googletag.defineSlot(slotPath, size, adSlotId).addService(googletag.pubads());
        }
        if (targeting) {
          Object.entries(targeting).forEach(_ref3 => {
            let [key, value] = _ref3;
            slotRef.current.setTargeting(key, value);
          });
        }

        // Add Adomik targeting parameters
        slotRef.current.setTargeting('ad_group', adomikRandomAdGroup());
        slotRef.current.setTargeting('ad_h', new Date().getUTCHours().toString());
        googletag.display(adSlotId);
        if (!lazyLoading) {
          refresh();
        } else if (lazyLoadingMethod === LAZYLOADING_METHODS.GoogleTag) {
          refreshLazyAd();
        }
        if (showPlaceholderRef.current) {
          if (!(window.googletag && window.googletag.apiReady && window.googletag.pubadsReady)) {
            showPlaceholderRef.current = true;
          } else {
            pubads = window.googletag.pubads();
            pubads.addEventListener('slotRenderEnded', handleSlotRenderEnded);
          }
        }
      });
    };
    if (window !== 'undefined' && window.googletag && context.isAdsScriptLoaded) {
      if (!path && !context.adUnit) {
        return () => {};
      }
      if (asyncInit > 0) {
        timeoutRef.current = setTimeout(() => {
          initSlot();
        }, asyncInit);
      } else {
        initSlot();
      }
    }
    let resizeObserver;
    if (multiSize && typeof window !== 'undefined' && 'ResizeObserver' in window) {
      const adslotContainerElement = document.getElementById(adSlotContainerId);
      if (!adslotContainerElement) {
        return undefined;
      }
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(adslotContainerElement);
    }
    return () => {
      if (slotRef.current) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        context.removeFromRenderedSlots(slotRef.current);
        if (googletag) {
          googletag.cmd.push(() => {
            googletag.destroySlots([slotRef.current]);
          });
        }
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (pubads) {
        pubads.removeEventListener('slotRenderEnded', handleSlotRenderEnded);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [global.googletag, context.isAdsScriptLoaded]);
  const renderAdContainer = () => {
    const width = size[0][0];
    const height = size[0][1];
    let maxWidth = width;
    let maxHeight = height;
    if (preserveMaxSize) {
      size.forEach(singleAdSize => {
        if (Array.isArray(singleAdSize)) {
          maxWidth = Math.max(maxWidth, singleAdSize[0]);
          maxHeight = Math.max(maxHeight, singleAdSize[1]) + 20;
        }
      });
    }
    const adParentClassNames = clsx({
      'display-block': showPlaceholderRef.current
    }, 'dfp-slot', className);
    const placeholderAttributes = {
      style: {
        minWidth: width,
        minHeight: height
      },
      className: 'bg-neutral-tertiary'
    };
    const wrapperStyle = {};
    if (preserveMaxSize) {
      wrapperStyle.height = maxHeight;
    }
    return /*#__PURE__*/React.createElement("div", Object.assign({
      "data-cmp": "adSlot",
      id: adSlotContainerId,
      className: adParentClassNames,
      style: {
        maxWidth: width,
        ...wrapperStyle
      }
    }, extra), /*#__PURE__*/_jsx("div", {}, void 0, /*#__PURE__*/_jsx(AdvertisementText, {
      componentClass: "div",
      className: clsx('dfp-label', advertisementLabel.className)
    }, "advertisement-text", advertisementLabel.text, advertisementLabel.enableHide && /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0\xA0|\xA0\xA0", /*#__PURE__*/_jsx(advertisementLabel.hideButton, {
      onClick: advertisementLabel.onClickHide
    }))), /*#__PURE__*/React.createElement("div", Object.assign({
      key: adSlotId,
      id: adSlotId
    }, showPlaceholderRef.current && placeholderAttributes))));
  };
  if (lazyLoading && lazyLoadingMethod === LAZYLOADING_METHODS.Waypoint) {
    return /*#__PURE__*/_jsx(WayPointAd, {
      adSlotId: `waypoint=${adSlotId}`,
      handleEnter: handleEnter,
      handleLeave: handleLeave,
      lazyLoadingTopOffset: lazyLoadingTopOffset,
      lazyLoadingBottomOffset: lazyLoadingBottomOffset,
      renderAdContainer: renderAdContainer
    });
  }
  return adSlotId && size ? renderAdContainer() : null;
}

// [300, 250], [[300, 250], 'fluid'], ['fluid']

/*
 [
 [
 [1300,0],
 [[1, 1], [970, 90]],
 ], [
 [1300, 0],
 [300, 250],
 ], [
 [1300, 0],
 'fluid',
 ],
 ]
 */

export default /*#__PURE__*/memo(AdSlot, (oldProps, newProps) => _isEqual(oldProps, newProps));